import { ReactiveVar, useReactiveVar } from '@apollo/client'
import { CurrentUserFirmsProps } from '@deltek/specification-client/core/Models/currentUserInfo'
import { UserInfo } from 'api/graphQL/reactiveVars/user'
import { showBestPracticeSetting } from 'api/graphQL/reactiveVars/workspaceToggles'
import { processEnv } from 'components/configuration/environmentVariables'
import { TrackChangeType } from 'models/changes'
import { NormalizedDocumentTemplateViewItem } from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import React from 'react'

export interface UseIsBestPracticeTagDisabledProps {
    userInfo: ReactiveVar<UserInfo>
    isInProject?: boolean
    contentTeamFromStudioPage?: boolean
    isUserAddedPR?: boolean
    isProjectImported?: boolean
    isProjectOnly?: boolean
    isFromImportedNugget?: boolean
    isViewOnly?: boolean
    firmsBPSetting?: boolean | null
}
/**
 * Determines whether the best practice tag should be disabled based on the user's information and context.
 * @param userInfo - The reactive variable containing the user's information.
 * @param isInProject - Optional. Indicates whether the element is added to a project.
 * @param contentTeamFromStudioPage - Optional. Indicates whether the user is on the studio page.
 * @param isUserAddedPR - Optional. Indicates whether the user added the element to the project.
 * @param isProjectImported - Optional. Indicates a project is AE Workspace/Project level imported.
 * @param isProjectOnly - Optional. Indicates an item belongs to current project only. (eg: copied PTs)
 * @param isViewOnly - Optional. Decides whether the BP tags is disabled for view or edit
 * @param isFromImportedNugget - Optional. Indicates a nugget is AE Workspace/Project level imported
 * @param firmsBPSetting - Optional. Overrides the default best practice setting. When null, uses the default reactive variable.
 * @returns A boolean value indicating whether the best practice tag should be disabled.
 */
export const useIsBestPracticeTagDisabled = ({
    userInfo,
    isInProject,
    contentTeamFromStudioPage,
    isUserAddedPR,
    isProjectImported,
    isProjectOnly,
    isFromImportedNugget,
    isViewOnly,
    firmsBPSetting,
}: UseIsBestPracticeTagDisabledProps): boolean => {
    const {
        isAdmin,
        isAuthor,
        isReviewer,
        isBestPracticeEnabled,
    } = useReactiveVar(userInfo)

    // Determines if Best Practices can be used. When true, the Best Practices menu, icons and BP-related elements will be shown.
    const defaultShowBPSetting = showBestPracticeSetting()
    const actualShowBPSetting =
        firmsBPSetting !== undefined ? firmsBPSetting : defaultShowBPSetting
    const isBPDisabled = React.useMemo(() => {
        const isUserAllowed = isViewOnly
            ? isAdmin || isAuthor || isReviewer
            : isAdmin || (isAuthor && isBestPracticeEnabled)

        // default to false when parameter is null/undefined so it doesn't affect the condition
        const notInProject =
            isInProject !== undefined && isInProject !== null
                ? !isInProject
                : false
        const fromStudioPage =
            contentTeamFromStudioPage !== undefined &&
            contentTeamFromStudioPage !== null
                ? contentTeamFromStudioPage
                : false

        return (
            // !showBPSetting || // TEMP: commented out for debugging - remove this check when BP tags is Production ready
            !actualShowBPSetting ||
            notInProject ||
            fromStudioPage ||
            !isUserAllowed ||
            !!isUserAddedPR ||
            !!isProjectImported ||
            !!isProjectOnly ||
            !!isFromImportedNugget
        )
    }, [
        isAdmin,
        isAuthor,
        isBestPracticeEnabled,
        isInProject,
        contentTeamFromStudioPage,
        isUserAddedPR,
        isProjectImported,
        isProjectOnly,
        isViewOnly,
        isFromImportedNugget,
        actualShowBPSetting,
    ])

    return isBPDisabled
}

/**
 * Determines whether the best practice setting pop-up should be shown based on the user's active firm setting.
 * @param currentUserActiveFirm - The reactive variable containing the user's active firm information.
 * @returns A boolean value indicating whether the best practice setting pop-up should be shown.
 */
export function canShowBestPracticeSetting(
    currentUserActiveFirm: CurrentUserFirmsProps
): boolean {
    return (
        // This is implemented here to make test case work
        // Remove this Environment Variable check when BP tags feature is ready for release #BPtagsRelease
        processEnv.REACT_APP_ShowBestPracticesSetting === '1' &&
        currentUserActiveFirm?.areBestPracticesEnabled
    )
}

/**
 * Determines wheather all element in given item is deleted..
 * @param item - The NormalizedDocumentTemplateViewItem containing reference of a paragraph.
 */
export function isAllElementDeleted(
    item: Partial<NormalizedDocumentTemplateViewItem>
): boolean {
    if (!!item.contentItem && (item.contentItem?.elements || [])?.length > 0) {
        return (item.contentItem.elements || []).every(
            (element) =>
                element.changeType?.toLowerCase() ==
                TrackChangeType.DELETED.toLowerCase()
        )
    }
    return false
}

/**
 * Determines whether element can be shown on screen.
 * @param item - The NormalizedDocumentTemplateViewItem containing reference of a paragraph.
 * @param isTrackChangeToggleOn - indicates that track change is toggled on/off.
 */
export function canElementBeShown(
    item: Partial<NormalizedDocumentTemplateViewItem>,
    isTrackChangeToggleOn: boolean
): boolean {
    if (isTrackChangeToggleOn) return true

    return !isAllElementDeleted(item)
}
